(function () {

  /* ---------------------------------------------------------
     FLOATING MENU SECTION
  ---------------------------------------------------------- */

  /* ─────────────────────────────────────────────────────────────
     iOS Safari SCROLL LOCK — touchmove preventDefault approach.

     WHY NOT position:relative/fixed + top:-scrollY + window.scrollTo?
       1. body.style.position = 'relative' + top:-scrollY visually fakes
          the scroll position but window.scrollY stays 0.
       2. On unlock: styles reset → body snaps to top visually.
       3. window.scrollTo(0, savedY) fires → visible page JUMP = "refresh".
       FIX: don't change body position at all. Block scroll via
       touchmove preventDefault — no position change, no scrollTo,
       no jump on close.
  ───────────────────────────────────────────────────────────── */
  var _iosLocked = false;
  var _iosPrevY = 0;

  function _iosTouchStart(e) {
    _iosPrevY = e.touches[0] ? e.touches[0].clientY : 0;
  }

  // Returns true only if element has overflow:auto/scroll AND actually overflows
  function _isScrollable(el) {
    var oy = window.getComputedStyle(el).overflowY;
    return (oy === 'auto' || oy === 'scroll') && el.scrollHeight > el.clientHeight;
  }

  function _iosTouchPrevent(e) {
    var currentY = e.touches[0] ? e.touches[0].clientY : 0;
    var deltaY = currentY - _iosPrevY;
    _iosPrevY = currentY;

    var modalContent = e.target && typeof e.target.closest === 'function'
      ? e.target.closest('.search-modal__content')
      : null;

    // Touch outside modal — block completely
    if (!modalContent) {
      e.preventDefault();
      return;
    }

    // Walk up from touch target to find the innermost TRULY scrollable element
    var scrollEl = e.target;
    while (scrollEl && scrollEl !== modalContent) {
      if (_isScrollable(scrollEl)) break;
      scrollEl = scrollEl.parentElement;
    }
    if (!scrollEl) scrollEl = modalContent;

    // If nothing scrollable found (including modalContent), block scroll
    if (!_isScrollable(scrollEl)) {
      e.preventDefault();
      return;
    }

    // Prevent iOS scroll chaining at top/bottom boundary
    var atTop = scrollEl.scrollTop <= 0;
    var atBottom = Math.ceil(scrollEl.scrollTop + scrollEl.clientHeight) >= scrollEl.scrollHeight;

    if ((atTop && deltaY > 0) || (atBottom && deltaY < 0)) {
      e.preventDefault();
    }
  }

  function iosLockScroll() {
    if (_iosLocked) return;
    document.addEventListener('touchstart', _iosTouchStart, { passive: true });
    document.addEventListener('touchmove', _iosTouchPrevent, { passive: false });
    _iosLocked = true;
  }

  function iosUnlockScroll() {
    if (!_iosLocked) return;
    document.removeEventListener('touchstart', _iosTouchStart);
    document.removeEventListener('touchmove', _iosTouchPrevent);
    _iosLocked = false;
  }

  class FloatingMenu extends HTMLElement {
    constructor() {
      super();
      this.state = { activePanel: null };
      this.lastActive = null;

      if ("requestIdleCallback" in window) {
        window.requestIdleCallback(() => {
          this.attachEventListeners();
          this.setupResizeObserver();
        });
      } else {
        setTimeout(() => {
          this.attachEventListeners();
          this.setupResizeObserver();
        }, 1);
      }
    }

    connectedCallback() {
      document.body.classList.add("has-floating-menu");

      if (!this.querySelector(".nav-indicator")) {
        const indicator = document.createElement("span");
        indicator.className = "nav-indicator";
        indicator.style.cssText =
          "position:absolute;bottom:0;height:2px;width:0;transition:transform .25s,width .25s,opacity .25s;background:currentColor;opacity:0;";
        this.appendChild(indicator);
      }

      const nav = document.querySelector(".floating-nav");
      if (nav) {
        // padding-bottom: var(--floating-menu-height) is applied by CSS
        // (floating_menus.css) with a static estimate default — this just
        // corrects the variable to the exact measured value.
        document.documentElement.style.setProperty("--floating-menu-height", nav.offsetHeight + "px");
      }

      this.setInitialActiveNav();
    }

    attachEventListeners() {
      this.addEventListener("click", this.handleClick.bind(this));
      document.addEventListener("keydown", this.handleKeyDown.bind(this));

      /* ── CART DRAWER CLOSE ── */
      document.body.addEventListener("click", (event) => {
        const closeBtn = event.target.closest(".kymee-drawer__close");
        if (!closeBtn) return;

        const cartDrawer = document.querySelector(".kymee-drawer");
        if (cartDrawer) cartDrawer.classList.remove("is-open");

        document.body.style.overflow = "";
        document.body.classList.remove("overflow-hidden", "overflow-hidden-tablet");

        this.state.activePanel = null;
        this.restoreAfterDrawer();
        this.setInitialActiveNav();
      });

      /* ── CATEGORY DRAWER CLOSE ── */
      document.body.addEventListener("click", (event) => {
        const closeBtn = event.target.closest(".category-drawer .drawer-close");
        if (!closeBtn) return;

        document.body.style.overflow = "";
        document.body.classList.remove("overflow-hidden", "overflow-hidden-tablet");

        this.state.activePanel = null;
        this.restoreAfterDrawer();
        this.setInitialActiveNav();
      });

      /* ── SEARCH CLOSE BUTTON (inside modal) ── */
      document.addEventListener("click", (event) => {
        const closeBtn =
          event.target.closest(".search-modal__close-button") ||
          event.target.closest('[data-action="close-search"]');
        if (!closeBtn) return;
        this._closeSearch();
      });

      /* ── BIDIRECTIONAL SYNC: keep body classes + button state in sync
            with the actual details[open] attribute, regardless of HOW the
            search was opened or closed (floating menu, native details toggle,
            Dawn's DetailsModal, ESC key, outside-click, timing race). ── */
      document.querySelectorAll(".header__search details").forEach((drawer) => {
        new MutationObserver(() => {
          const isOpen = drawer.hasAttribute("open");
          const searchBtn = this.querySelector('[data-nav="search"]');

          if (isOpen) {
            iosLockScroll();
            if (searchBtn) searchBtn.classList.add("active");
          } else {
            iosUnlockScroll();
            if (searchBtn) {
              searchBtn.classList.remove("active");
              this.clearActive();
              this.restoreAfterDrawer();
            }
          }
        }).observe(drawer, { attributes: true, attributeFilter: ["open"] });
      });

      this.updateCartCount();
      this.onCartUpdated = this.updateCartCount.bind(this);
      document.addEventListener("kymee:cart:added", this.onCartUpdated);
      document.addEventListener("cart:updated", this.onCartUpdated);
    }

    disconnectedCallback() {
      document.removeEventListener("kymee:cart:added", this.onCartUpdated);
      document.removeEventListener("cart:updated", this.onCartUpdated);
    }

    setupResizeObserver() {
      if ("ResizeObserver" in window) {
        const ro = new ResizeObserver(() => {
          if (this.state.activePanel) this.positionPanel();
        });
        ro.observe(document.body);
      }
    }

    getPageState() {
      const path = window.location.pathname;
      return { isHome: path === "/" || path === "/index" };
    }

    /* --------------------------------------------------------
       SEARCH HELPERS
    -------------------------------------------------------- */

    /**
     * Returns the first visible .header__search <details> element.
     * Iterates all instances (header-search renders multiple times)
     * and picks the one whose computed display is not 'none'.
     */
    _getSearchDrawer() {
      const drawers = document.querySelectorAll(".header__search details");
      for (const d of drawers) {
        if (window.getComputedStyle(d).display !== "none") return d;
      }
      // Fallback: return first if all appear hidden (e.g. during page transition)
      return drawers[0] || null;
    }

    _closeSearch() {
      const drawer = document.querySelector(".header__search details[open]") ||
                     this._getSearchDrawer();
      if (!drawer) return;

      const detailsModal = drawer.closest('details-modal');
      if (detailsModal && typeof detailsModal.close === 'function') {
        detailsModal.close(false);
      } else {
        drawer.removeAttribute("open");
        document.body.classList.remove("overflow-hidden", "search-open");
        iosUnlockScroll();
      }
      this.clearActive();
      this.restoreAfterDrawer();
    }

    /* --------------------------------------------------------
       MAIN CLICK HANDLER
    -------------------------------------------------------- */
    handleClick(event) {
      const target = event.target.closest("[data-nav], [data-action]");
      if (!target) return;

      const panel = target.dataset.nav;
      const action = target.dataset.action;
      const home = this.querySelector('.nav-item[data-nav="home"]');

      /* HOME on home page */
      if (panel === "home" && this.getPageState().isHome) {
        event.preventDefault();
        this.closePanel();
        this.restoreTo(home);
        return;
      }

      /* CLOSE action */
      if (action === "close") {
        this.closePanel();
        return;
      }

      /* CART */
      if (panel === "cart") {
        event.preventDefault();
        this.closePanel();

        const cartDrawer = document.querySelector(".kymee-drawer");
        if (cartDrawer) {
          cartDrawer.classList.add("is-open");
          document.body.classList.add("overflow-hidden");
        }

        this.updateActiveState(target);
        return;
      }

      /* SEARCH — toggle open / close */
      if (panel === "search") {
        event.preventDefault();

        const searchDrawer = this._getSearchDrawer();
        if (!searchDrawer) return;

        // Treat "open + closing animation in progress" the same as closed,
        // so a tap during the 300ms animation re-opens instead of closing again.
        const isClosing = !!searchDrawer.querySelector('.search-modal--closing');

        if (searchDrawer.hasAttribute("open") && !isClosing) {
          /* ── CLOSE ── */
          this._closeSearch();
        } else {
          /* ── OPEN (or cancel close + re-open) ── */
          this.closePanel();

          const detailsModal = searchDrawer.closest('details-modal');
          if (detailsModal && typeof detailsModal.open === 'function') {
            detailsModal.open();
          } else {
            searchDrawer.setAttribute("open", "");
            document.body.classList.add("overflow-hidden", "search-open");
          }
          this.updateActiveState(event.target.closest("[data-nav]"));
        }
        return;
      }

      /* Other nav items */
      this.updateActiveState(target);
    }

    handleKeyDown(event) {
      if (event.key === "Escape") {
        this.closePanel();
        this._closeSearch();
      }
    }

    togglePanel(panel) {
      this.state.activePanel === panel ? this.closePanel() : this.openPanel(panel);
    }

    openPanel(panel) {
      this.closePanel();
      this.state.activePanel = panel;

      const el = document.getElementById(
        `${panel.charAt(0).toUpperCase() + panel.slice(1)}Drawer`
      );
      if (el) {
        el.classList.add("active");
        el.removeAttribute("aria-hidden");
        document.body.style.overflow = "hidden";
      }
    }

    closePanel() {
      if (!this.state.activePanel) return;

      const panel = this.state.activePanel;
      const el = document.getElementById(
        `${panel.charAt(0).toUpperCase() + panel.slice(1)}Drawer`
      );

      if (el) {
        el.classList.remove("active");
        el.setAttribute("aria-hidden", "true");
      }

      this.restoreAfterDrawer();
      this.state.activePanel = null;
      document.body.style.overflow = "";
      document.body.classList.remove("overflow-hidden", "overflow-hidden-tablet");
    }

    restoreAfterDrawer() {
      const indicator = this.querySelector(".nav-indicator");
      const { isHome } = this.getPageState();

      this.clearActive();

      if (isHome) {
        const h = this.querySelector('.nav-item[data-nav="home"]');
        if (h) {
          h.classList.add("active");
          this.moveIndicator(h, indicator);
          indicator.style.opacity = "1";
        }
      } else {
        indicator.style.opacity = "0";
      }
    }

    clearActive() {
      this.querySelectorAll(".nav-item[data-nav]").forEach((i) =>
        i.classList.remove("active")
      );
    }

    restoreTo(item) {
      const indicator = this.querySelector(".nav-indicator");
      this.clearActive();
      item.classList.add("active");
      indicator.style.opacity = "1";
      this.moveIndicator(item, indicator);
    }

    setInitialActiveNav() {
      const indicator = this.querySelector(".nav-indicator");
      const home = this.querySelector('.nav-item[data-nav="home"]');
      const { isHome } = this.getPageState();

      indicator.style.opacity = "0";

      setTimeout(() => {
        if (isHome && home) {
          home.classList.add("active");
          indicator.style.opacity = "1";
          this.moveIndicator(home, indicator);
        }
      }, 80);
    }

    updateActiveState(clickedItem) {
      if (!clickedItem) return;
      const indicator = this.querySelector(".nav-indicator");
      this.clearActive();
      clickedItem.classList.add("active");
      indicator.style.opacity = "1";
      this.moveIndicator(clickedItem, indicator);
    }

    moveIndicator(item, indicator) {
      const navRect = this.querySelector(".floating-nav").getBoundingClientRect();
      const itemRect = item.getBoundingClientRect();
      const offset =
        itemRect.left - navRect.left + itemRect.width / 2 - indicator.offsetWidth / 2;
      indicator.style.transform = `translateX(${offset}px)`;
    }

    positionPanel() {
      if (!this.state.activePanel) return;
      const el = document.getElementById(
        `${this.state.activePanel.charAt(0).toUpperCase() + this.state.activePanel.slice(1)}Drawer`
      );
      if (el) el.offsetHeight;
    }

    updateCartCount() {
      fetch("/cart.js")
        .then((r) => r.json())
        .then((cart) => {
          const count = cart.item_count;
          document.querySelectorAll("[data-cart-count]").forEach((el) => {
            el.textContent = count;
            el.classList.toggle("show", count > 0);
          });
        })
        .catch(() => {});
    }
  }

  if (!customElements.get("floating-menu")) {
    customElements.define("floating-menu", FloatingMenu);
  }
})();
